import { useCallback, useState } from 'react';

import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, Spin, Tooltip } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';

import { IHotel, IRoom, ISessionKey } from '@share/common-types';
import { RefundableEnum } from '@common-types';
import { hotelsActions, marginatorActions } from '@share/store/slices';
import {
  FunctionalRootState,
  formatDateCheckInOut,
  renderNumber,
  UrlUtils,
  LoginType,
  GetDisableNewTabOpen,
  isSpanish,
} from '@share/utils';
import { hotelDetailsActions, roomsSearchActions } from '@store/slices';
import { AVAILABILITY_SOLDOUT } from '@share/constants';
import { getAmenityImage, openHotelPage, HasPermission, ViewQuotesSharePermission } from '@utils';
import { Currency, HotelStars, ModalShare } from '@components';
import { BlueButton } from '@share/components';
import { DATE_FORMAT_CHECK_IN_OUT, LOCATION_OBJECT_LABEL, NULL_VALUE } from '@constants';
import { DownLoadShareType, HandleDownload } from '@share/api';
import { es } from 'date-fns/esm/locale';
import NoImage from '@assets/images/amenities/no-picture.png';
import {
  getModalCompareMealPlanBadgeKind,
  MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS,
} from './meal-plan-badge-theme';
import './style.scss';

const modalWidth = 900;
const percents = 100;

export interface ModalCompareProps {
  visible: boolean;
  rooms: IRoom[];
  checkIn: string | null | undefined;
  checkOut: string | null | undefined;
  locationCode: string;
  sessionKey: ISessionKey;
  hotels: IHotel[];
  isVacationRentals?: boolean | null | undefined;

  onCancel: () => void;
  onRemove: (hotel: IHotel) => void;
}

export function ModalCompare({
  visible,
  rooms,
  checkIn,
  checkOut,
  locationCode,
  sessionKey,
  hotels,
  isVacationRentals,
  onCancel,
  onRemove,
}: ModalCompareProps) {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();

  const marginatorStore = useSelector((state: FunctionalRootState) => state.marginatorStore);
  const { account, user } = useSelector((state: FunctionalRootState) => state.loginStore);
  const locationsStore = useSelector((state: FunctionalRootState) => state.locationsStore);
  const menuStore = useSelector((state: FunctionalRootState) => state.navigationMenuStore);

  const [loading, setLoading] = useState(false);
  const [visibleAction, setVisibleAction] = useState(false);
  const [isShare, setIsShare] = useState(false);

  const handleView = (hotel: IHotel, marginatorCommission: number) => {
    const { hotelId, availability } = hotel;
    dispatch(marginatorActions.setCommission(marginatorCommission));
    dispatch(marginatorActions.setSupplier(hotel.supplierId));
    if (availability !== AVAILABILITY_SOLDOUT) {
      dispatch(hotelDetailsActions.setHotelDetails(NULL_VALUE));
      dispatch(roomsSearchActions.resetState());
      dispatch(hotelsActions.setSelectedHotel(hotel));

      const disableNewTabOpen = GetDisableNewTabOpen(account, menuStore.items);

      openHotelPage(
        hotelId,
        history,
        disableNewTabOpen as boolean,
        account?.isTravelAgentPro,
        isVacationRentals,
        account?.forceIframe,
      );
    }
  };

  const handleData = useCallback(
    (members?: string[], comment?: string) => {
      const value = marginatorStore.value || 0;

      const search = UrlUtils.getUrl(LOCATION_OBJECT_LABEL, locationsStore.selectedLocation);
      const locationObject = search.split('&').find((x) => x.includes('locationObject'))?.substring(15);
      const isB2C = account?.isB2C;

      const data = {
        quotes: hotels.map((hotel) => {
          const marginatorPrice = hotel.price / (1 - value / 100);
          const savingsNunmber = hotel.savings / marginatorPrice * 100;
          const discount = savingsNunmber ? Math.floor(savingsNunmber) : 0;
          const publicPrice =
            isB2C || !hotel.publicPrices?.length ? null : Math.min(...hotel.publicPrices.map((p) => p.price));

          return {
            hotelId: hotel.hotelId,
            displayName: hotel.displayName,
            fullAddress: `${hotel.address}, ${hotel.city}, ${hotel.state}, ${hotel.countryCode}`,
            mealPLan: hotel.mealPLan,
            refundability: hotel.refundability,
            starRating: hotel.starRating,
            reviewsCount: hotel.rating?.reviewsCount,
            rating: hotel.rating?.value,
            currency: hotel.currency,
            price: marginatorPrice,
            publicPrice,
            discount,
            image: get(hotel, 'images[0].url'),
            amenities: hotel?.amenities?.map((a) => a.name),
          };
        }),
        comment,
        members,
        location: locationObject,
        hotelRequest: {
          checkIn,
          checkOut,
          location: locationCode,
          rooms: rooms.map((r) => ({
            adultsCount: r.adultsCount,
            kidsAges: r.kids?.map((k) => k.age || 0),
          })),
        },
        sessionKey,
      };

      return data;
    },
    [
      marginatorStore.value,
      locationsStore.selectedLocation,
      account?.isB2C,
      hotels,
      checkIn,
      checkOut,
      locationCode,
      rooms,
      sessionKey,
    ],
  );

  const handleDownload = () => {
    const prevLoading = loading;
    setLoading(true);
    HandleDownload(DownLoadShareType.HOTEL, prevLoading, handleData(), intl, () => setLoading(false), () =>
      setVisibleAction(false),
    );
  };

  const adults = rooms?.length
    ? rooms.map((room) => room.adultsCount || 0).reduce((acc, curr) => acc + curr, 0)
    : 0;
  const childs = rooms?.length ? rooms.map((room) => room.kids?.length || 0).reduce((acc, curr) => acc + curr, 0) : 0;
  const isB2C = account?.isB2C;

  const fontFamily = account?.fontFamily;
  const secondaryFontFamily = account?.secondaryFontFamily;

  const compareRowHasMealPlan = hotels.some((h) => !isEmpty(h.mealPLan?.trim()));

  return (
    <Modal
      className={`modal-compare ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      open={visible}
      footer={null}
      onCancel={onCancel}
      wrapClassName="modal-compare__wrapper"
      width={modalWidth}
    >
      <div>
        <div className="modal-compare__title-row">
          <h4 className="modal-compare__title secondary-font">
            <FormattedMessage id="results.hotel.card.compare.modal.title" />
          </h4>
        </div>

        <div className="modal-compare__hotels modal-compare__hotels--meta">
          <div className="hotels-dates">
            {formatDateCheckInOut(checkIn, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)} -{' '}
            {formatDateCheckInOut(checkOut, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)}
          </div>
          <div className="hotels-dates">
            {rooms?.length} <FormattedMessage id="room" values={{ count: rooms?.length }} /> - {adults}{' '}
            <FormattedMessage id="adult" values={{ count: adults }} /> - {childs}{' '}
            <FormattedMessage id="child" values={{ count: childs }} />
          </div>
        </div>

        <div className="modal-compare__hotels">
          {hotels.map((hotel, index) => {
            const { price } = hotel;

            const value = marginatorStore.value || 0;
            const marginatorPrice = price / (1 - value / 100);
            const marginatorCommission = marginatorPrice - price;

            const publicPrice =
              isB2C || !hotel.publicPrices?.length ? null : Math.min(...hotel.publicPrices.map((p) => p.price));

            const msave = publicPrice ? (marginatorPrice * percents) / publicPrice : 100;
            const savings = Math.floor(percents - msave);

            return (
              <div key={index} className="modal-compare__hotel">
                <div className="modal-compare__media">
                  <div className="hotel-images-container">
                    <div className="hotel-image" style={{ backgroundImage: `url(${get(hotel, 'images[0].url')})` }} />
                    {savings > 0 ? (
                      <div className="modal-compare__savings-badge">
                        {savings.toFixed(0)} % <FormattedMessage id="results.hotel.card.compare.modal.off" />
                      </div>
                    ) : null}
                  </div>
                  <label
                    className={`modal-compare__remove-btn ${hotels.length === 2 ? 'disabled' : ''}`}
                    onClick={() => {
                      if (hotels.length > 2) {
                        onRemove(hotel);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleXmark} className="modal-compare__remove-btn-icon" />
                  </label>
                </div>

                <div
                  className={`hotel-text-container hotel-text-container--title${
                    compareRowHasMealPlan ? ' hotel-text-container--title-with-mealplan' : ''
                  }`}
                >
                  {compareRowHasMealPlan ? (
                    <div className="modal-compare__title-mealplan-row">
                      <div className="modal-compare__title-col">
                        <Tooltip title={hotel.displayName} placement="top">
                          <p>{hotel.displayName}</p>
                        </Tooltip>
                      </div>
                      <div className="modal-compare__meal-plan-col">
                        {!isEmpty(hotel.mealPLan?.trim()) ? (
                          <span
                            className="modal-compare__meal-plan-badge"
                            style={
                              MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS[
                                getModalCompareMealPlanBadgeKind(hotel.mealPLan)
                              ]
                            }
                          >
                            {hotel.mealPLan}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <Tooltip title={hotel.displayName} placement="top">
                      <p>{hotel.displayName}</p>
                    </Tooltip>
                  )}
                </div>

                <div className="hotel-text-container hotel-text-container--address">
                  <Tooltip
                    title={`${hotel.address}, ${hotel.city}, ${hotel.state}, ${hotel.countryCode}`}
                    placement="top"
                  >
                    <p>
                      {hotel.address}, {hotel.city}, {hotel.state}, {hotel.countryCode}
                    </p>
                  </Tooltip>
                </div>

                {hotel.refundability && hotel.refundability !== RefundableEnum.Unknown ? (
                  <div className="hotel-text-container hotel-text-container--refundable">
                    <div className="hotel-card-wrapper__refundable">
                      {hotel.refundability === RefundableEnum.Refundable && (
                        <div className="hotel-card-wrapper__refundable-ref">
                          <FormattedMessage id="refundable" />
                        </div>
                      )}
                      {hotel.refundability === RefundableEnum.Nonrefundable && (
                        <div className="hotel-card-wrapper__refundable-nonref">
                          <FormattedMessage id="non.refundable" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {hotel.starRating >= 0 || hotel.rating?.value || hotel.rating?.reviewsCount ? (
                  <div className="hotel-amenities-container">
                    {hotel.starRating >= 0 ? <HotelStars stars={hotel.starRating} /> : null}
                    <label>
                      {hotel.rating?.value ? (
                        <>
                          <strong>{hotel.rating?.value}</strong>/10
                        </>
                      ) : null}{' '}
                      {hotel.rating?.reviewsCount ? (
                        <>
                          ({hotel.rating?.reviewsCount} <FormattedMessage id="reviews" />)
                        </>
                      ) : null}
                    </label>
                  </div>
                ) : null}

                <div className="hotel-text-container hotel-text-container--price">
                  {publicPrice ? (
                    <p style={{ textDecoration: 'line-through' }}>
                      <Currency currency={hotel.currency} />
                      {renderNumber(publicPrice, 2)}
                    </p>
                  ) : null}
                  <p>
                    <strong>
                      <Currency currency={hotel.currency} />
                      {renderNumber(marginatorPrice, 2)}
                    </strong>
                  </p>
                </div>

                <div className="hotel-text-container" style={{ height: '87px' }}>
                  {hotel?.amenities?.length ? (
                    <>
                      {hotel?.amenities?.filter((a) => !isEmpty(a))?.length > 0 ? (
                        hotel
                          ?.amenities.map((a) => ({ ...(getAmenityImage(a) as Record<string, unknown>), name: a.name }))
                          .filter((a) => !!a)
                          .slice(0, 2)
                          .map((a, i) => (
                            <div key={i} className="modal-compare__amenity-row">
                              <img src={(a as { image?: string }).image || NoImage} alt={a.name || 'no-picture'} width="33" />
                              <div className="modal-compare__amenity-label-wrap">
                                <Tooltip title={a.name || ''} placement="top">
                                  <span className="modal-compare__amenity-name">{a.name}</span>
                                </Tooltip>
                              </div>
                            </div>
                          ))
                      ) : (
                        <FormattedMessage id="results.hotel.card.compare.modal.no_amenities" />
                      )}
                    </>
                  ) : (
                    <FormattedMessage id="results.hotel.card.compare.modal.no_amenities" />
                  )}
                </div>

                <div className="hotel-view-container">
                  <div className="view-button" onClick={() => handleView(hotel, marginatorCommission)}>
                    <FormattedMessage id="results.hotel.card.compare.modal.view" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-compare__buttons">
          {!account || account.type !== LoginType.PrivateWithToken ? (
            account?.displayShareCompareHotels && (
              <BlueButton disabled={loading} onClick={handleDownload}>
                {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
              </BlueButton>
            )
          ) : (
            <>
              {account?.displayShareCompareHotels && (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(false);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
                </BlueButton>
              )}

              {HasPermission(user, ViewQuotesSharePermission) ? (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(true);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.share" />}
                </BlueButton>
              ) : null}
            </>
          )}
        </div>
      </div>

      <ModalShare
        visible={visibleAction}
        isShare={isShare}
        type={DownLoadShareType.HOTEL}
        onCancel={() => setVisibleAction(false)}
        onLoading={(v) => setLoading(v)}
        convertData={handleData}
      />
    </Modal>
  );
}
